import { AfterViewInit, Component, EventEmitter, Input, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { ModalV2Component } from "../../../../../shared/modal-v2/modal-v2.component";
import { DiaryForm } from "../../../../../core/models/trial-diary-form/diary-form.model";
import { SelectOption } from "../../../../../core/models/select-option.model";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import {
  noWhitespaceValidator,
  requiredNoWhitespaceValidator
} from "../../../../../core/validators/no-whitespace.validator";
import { DiaryFormElementType } from "../../../../../core/constants/diary-form-element-type.enum";
import {
  DiaryFormConditionExpression,
  DiaryFormRule
} from "../../../../../core/models/trial-diary-form/diary-form-rule.model";
import { SegmentControlComponent } from "../../../../../shared/segment-control/segment-control.component";
import { InputSelectComponent } from "../../../../../shared/input-select/input-select.component";
import { Cultures } from "../../../../../core/constants/cultures";
import {
  DiaryFormRuleTranslation
} from "../../../../../core/models/trial-diary-form/diary-form-rule-tranlsation.model";
import { BaseComponent } from "../../../../../shared/base-component";

@Component({
  selector: 'app-trial-diary-add-rule-modal',
  templateUrl: './trial-diary-add-rule-modal.component.html',
  styleUrl: './trial-diary-add-rule-modal.component.scss',
  encapsulation: ViewEncapsulation.None
})
export class TrialDiaryAddRuleModalComponent extends BaseComponent implements AfterViewInit {
  @ViewChild('modal') modal: ModalV2Component;
  @ViewChild('segmentControl') segmentControl: SegmentControlComponent;
  @ViewChild('cultureSelect') cultureSelect: InputSelectComponent;

  @Output() updated = new EventEmitter();

  @Input() diaryForm: DiaryForm;
  @Input() editing: boolean = false;

  protected diaryFormRule: DiaryFormRule = new DiaryFormRule();

  protected availableCultures: { value: string, text: string }[] = Cultures.all().map(c => ({ value: c.culture, text: c.name }));
  protected filteredCultures: { value: string, text: string }[] = this.availableCultures;
  protected currentSegment = 0;
  protected elementOptions: SelectOption[] = [];
  protected logicalOperationOptions: SelectOption[] = [
    new SelectOption('0', 'Logical Operator: AND'),
    new SelectOption('1', 'Logical Operator: OR')
  ];
  protected actionOptions: SelectOption[] = [
    new SelectOption('0', 'Show Message'),
    new SelectOption('1', 'Send Email To Site')
  ];
  protected filteredActionOptions: SelectOption[] = this.actionOptions;
  protected textStyleOptions: SelectOption[] = [
    new SelectOption('danger', 'Danger (red)'),
    new SelectOption('success', 'Success (green)'),
    new SelectOption('info', 'Information')
  ];

  protected tokens: { key: string, description: string }[] = [
    { key: 'trial_code', description: 'Trial Code' },
    { key: 'patient_code', description: 'Patient Code' },
    { key: 'site_phone', description: 'Site Telephone Number' }
  ];

  protected cultureForms: FormGroup[] = [];
  protected form = new FormGroup({
    id: new FormControl<string>(null),
    selectedCulture: new FormControl<string>('en'),
    name: new FormControl<string>(null, [requiredNoWhitespaceValidator()]),
    description: new FormControl<string>(null, noWhitespaceValidator()),
    action: new FormControl<string>('0', [Validators.required]),
    messageText: new FormControl<string>(null),
    messageStyle: new FormControl<string>('info'),
    emailSubject: new FormControl<string>(null),
    emailText: new FormControl<string>(null),
    expressions: new FormControl<DiaryFormConditionExpression[]>([]),
    logicalOperator: new FormControl<string>('0'),
    ignoreConditions: new FormControl<boolean>(false),
    showWhenEntryIncomplete: new FormControl<boolean>(false),
    showWhenEntryComplete: new FormControl<boolean>(false),
  });

  ngAfterViewInit() {
    this.initCultures();

    this.form.get('action').valueChanges.subscribe(value => {
      this.form.patchValue({selectedCulture: 'en'});

      if (value === '0') {
        // Action: Show Message
        this.form.get('messageText').setValidators([requiredNoWhitespaceValidator()]);
        this.form.get('emailSubject').clearValidators();
        this.form.get('emailText').clearValidators();
      } else {
        // Action: Send Email
        this.form.get('messageText').clearValidators();
        this.form.get('emailSubject').setValidators([requiredNoWhitespaceValidator()]);
        this.form.get('emailText').setValidators([requiredNoWhitespaceValidator()]);
      }

      this.form.get('messageText').updateValueAndValidity();
      this.form.get('emailSubject').updateValueAndValidity();
      this.form.get('emailText').updateValueAndValidity();

      this.cultureForms.forEach(cultureForm => {
        if (value === '0') {
          // Action: Show Message
          cultureForm.get('messageText').setValidators([noWhitespaceValidator()]);
          cultureForm.get('emailSubject').clearValidators();
          cultureForm.get('emailText').clearValidators();
        } else {
          // Action: Send Email
          cultureForm.get('messageText').clearValidators();
          cultureForm.get('emailSubject').setValidators([noWhitespaceValidator()]);
          cultureForm.get('emailText').setValidators([noWhitespaceValidator()]);
        }

        cultureForm.get('messageText').updateValueAndValidity();
        cultureForm.get('emailSubject').updateValueAndValidity();
        cultureForm.get('emailText').updateValueAndValidity();
      });
    });

    this.setFilteredActions();

    this.form.get('ignoreConditions').valueChanges.subscribe(ignoreConditions => {
      this.setFilteredActions();
    });
  }

  private setFilteredActions() {
    if (this.form.get('ignoreConditions').value) {
      this.filteredActionOptions = [
        new SelectOption('0', 'Show Message')
      ];
      this.form.patchValue({action: '0'});
    } else {
      this.filteredActionOptions = [
        new SelectOption('0', 'Show Message'),
        new SelectOption('1', 'Send Email To Site')
      ];
    }
  };

  private resetForm() {
    this.form.patchValue({
      id: null,
      selectedCulture: 'en',
      name: null,
      description: null,
      action: '0', // Send Message
      messageText: null,
      emailSubject: null,
      emailText: null,
      expressions: [],
      messageStyle: 'info',
      logicalOperator: '0', // AND
      ignoreConditions: false,
      showWhenEntryIncomplete: false,
      showWhenEntryComplete: false
    });
  }

  /**
   * Hide the culture select if action is 1 (Send site email) or if current segment is not 2 (Action)
   *
   * @returns {boolean}
   */
  shouldHideCultureSelect() {
    if (this.currentSegment !== 2 || this.form.get('action').value === '1')
      return true;

    return false;
  }

  initCultures() {
    this.filteredCultures = this.availableCultures;
  }

  initElementOptions() {
    this.elementOptions = [];
    this.diaryForm.elements.forEach(element => {
      if (element.elementType === DiaryFormElementType.Dropdown || element.elementType === DiaryFormElementType.Radio || element.elementType === DiaryFormElementType.Checkbox) {
        this.elementOptions.push({value: element.id.toString(), text: element.label});
      }
    });
  }

  onSelectLanguage(language: string) {
    const culture = this.availableCultures.find(t => t.text === language);
    if (culture) {
      this.form.patchValue({selectedCulture: culture.value});
    }
  }

  onCultureFiltered(value: string) {
    // Trim the input value and convert it to lowercase for case-insensitive filtering
    const filterValue = value.trim().toLowerCase();

    this.filteredCultures = this.availableCultures.filter(x =>
      x.text.toLowerCase().includes(filterValue)
    );
  }

  add(diaryForm: DiaryForm) {
    this.resetForm();
    this.segmentControl.selectSegmentAtIndex(0);
    this.diaryForm = diaryForm;
    this.diaryFormRule = new DiaryFormRule();
    this.editing = false;
    this.initElementOptions();

    this.buildCultureForms();

    this.modal.show();
  }

  edit(diaryForm: DiaryForm, rule: DiaryFormRule) {
    this.segmentControl.selectSegmentAtIndex(0);
    this.diaryForm = diaryForm;
    this.diaryFormRule = rule;
    this.editing = true;
    this.initElementOptions();

    this.form.patchValue({
      id: rule.id,
      selectedCulture: 'en',
      name: rule.name,
      description: rule.description,
      action: rule.action.toString(),
      messageStyle: rule.messageStyle,
      messageText: rule.messageText,
      emailSubject: rule.emailSubject,
      emailText: rule.emailText,
      expressions: rule.expressions,
      ignoreConditions: rule.ignoreConditions,
      showWhenEntryIncomplete: rule.showWhenEntryIncomplete,
      showWhenEntryComplete: rule.showWhenEntryComplete,
      logicalOperator: rule.logicalOperator.toString()
    });

    this.buildCultureForms();

    this.modal.show();
  }

  buildCultureForms() {
    // Build culture forms
    this.cultureForms = [];
    this.availableCultures.forEach(culture => {
      const translation = this.diaryFormRule.translations.find(t => t.culture === culture.value);
      if (culture.value !== 'en') {
        this.cultureForms.push(new FormGroup({
          id: new FormControl<string>(translation?.id),
          diaryFormRuleId: new FormControl<string>(this.diaryFormRule.id),
          culture: new FormControl<string>(culture.value),
          messageText: new FormControl<string>(translation?.messageText, noWhitespaceValidator()),
          emailSubject: new FormControl<string>(translation?.emailSubject, noWhitespaceValidator()),
          emailText: new FormControl<string>(translation?.emailText, noWhitespaceValidator())
        }));
      }
    });
  }

  hide() {
    this.modal.hide();
  }

  onAddCondition() {
    this.form.get('expressions').value.push(new DiaryFormConditionExpression());
  }

  onRemoveCondition(index: number) {
    this.form.get('expressions').value.splice(index, 1);
  }

  segmentIndexChanged(index: number) {
    this.currentSegment = index;
  }

  isApplyButtonDisabled() {
    if (this.form.invalid)
      return true;

    if (this.form.get('ignoreConditions').value === false) {
      if (this.form.get('expressions').value.length === 0)
        return true;

      // Check all expressions are valid
      for (const expression of this.form.get('expressions').value) {
        if (!expression.isValid())
          return true;
      }
    } else {
      if (this.form.get('showWhenEntryIncomplete').value === false && this.form.get('showWhenEntryComplete').value === false)
        return true;
    }

    if (!this.cultureForms.every(f => f.valid))
      return true

    return false;
  }

  onApply() {
    if (this.form.invalid)
      return;

    if (!this.diaryForm.rules) {
      this.diaryForm.rules = [];
    }

    this.diaryFormRule.diaryFormId = this.diaryForm.id;
    this.diaryFormRule.name = this.form.get('name').value;
    this.diaryFormRule.description = this.form.get('description').value;
    this.diaryFormRule.action = +this.form.get('action').value;
    this.diaryFormRule.messageText = this.form.get('messageText').value;
    this.diaryFormRule.emailSubject = this.form.get('emailSubject').value;
    this.diaryFormRule.emailText = this.form.get('emailText').value;
    this.diaryFormRule.expression = JSON.stringify(this.form.get('expressions').value);
    this.diaryFormRule.expressions = this.form.get('expressions').value;
    this.diaryFormRule.logicalOperator = +this.form.get('logicalOperator').value;
    this.diaryFormRule.messageStyle = this.form.get('messageStyle').value;
    this.diaryFormRule.ignoreConditions = this.form.get('ignoreConditions').value;
    this.diaryFormRule.showWhenEntryIncomplete = this.form.get('showWhenEntryIncomplete').value;
    this.diaryFormRule.showWhenEntryComplete = this.form.get('showWhenEntryComplete').value;
    this.diaryFormRule.translations = [];

    // Add translations
    this.cultureForms.forEach(cultureForm => {
      this.diaryFormRule.translations.push(new DiaryFormRuleTranslation({
        culture: cultureForm.value.culture,
        messageText: cultureForm.value.messageText,
        emailSubject: cultureForm.value.emailSubject,
        emailText: cultureForm.value.emailText
      }));
    });

    // Add new rule
    if (!this.editing) {
      this.diaryForm.rules.push(this.diaryFormRule);
    }

    this.updated.emit();
    this.hide();
  }

}
